import { Link } from "@tanstack/react-router";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";
import { LogoIcon } from "./logo-icon";

const logoVariants = cva(
	"inline-flex items-center whitespace-nowrap leading-none",
	{
		variants: {
			size: {
				sm: "gap-2",
				md: "gap-2.5",
				lg: "gap-3",
				xl: "gap-4",
			},
		},
		defaultVariants: {
			size: "md",
		},
	},
);

const logoIconVariants = cva("aspect-square shrink-0", {
	variants: {
		size: {
			sm: "h-8",
			md: "h-9",
			lg: "h-10",
			xl: "h-11",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

const logoTextVariants = cva("font-semibold tracking-tight", {
	variants: {
		size: {
			sm: "text-base",
			md: "text-lg",
			lg: "text-xl",
			xl: "text-2xl",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

export interface LogoProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof logoVariants> {
	showText?: boolean;
	showDomain?: boolean;
	showIcon?: boolean;
	disableLink?: boolean;
	to?: "/" | "/dashboard";
}

const Logo = React.forwardRef<HTMLDivElement, LogoProps>(
	(
		{
			className,
			size = "md",
			showText = true,
			showDomain = false,
			showIcon = true,
			disableLink = false,
			to = "/",
			...props
		},
		ref,
	) => {
		const wordmark = showDomain
			? "AgentFramer.com"
			: showText
				? "AgentFramer"
				: "AF";
		const content = (
			<>
				{showIcon ? (
					<LogoIcon className={logoIconVariants({ size })} />
				) : null}
				<span
					className={cn(logoTextVariants({ size }), "text-foreground")}
					style={{
						fontFamily: "'Geist', -apple-system, sans-serif",
						textRendering: "geometricPrecision",
					}}
				>
					{wordmark}
				</span>
			</>
		);

		const wrapperClass = cn(logoVariants({ size }), className);

		if (disableLink) {
			return (
				<div ref={ref} className={wrapperClass} {...props}>
					{content}
				</div>
			);
		}

		return (
			<Link to={to} className={cn(wrapperClass, "cursor-pointer")}>
				{content}
			</Link>
		);
	},
);
Logo.displayName = "Logo";

export { Logo, logoVariants };
